import {
  IconCalendarMinus,
  IconCalendarPlus,
  IconCalendarRepeat,
  IconChecklist,
  IconCircleCheck,
  IconEdit,
  IconFile,
  IconFolder,
  IconFolderPlus,
  IconFolderSymlink,
  IconPencil,
  IconTrash,
  IconUserEdit,
  IconUserPlus,
} from '@tabler/icons-react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import { Link } from 'react-router-dom';
import remarkGfm from 'remark-gfm';

import {
  AgentToolResult,
  documentsLink,
  folderLabel,
  getAgentToolResult,
} from 'src/Components/Agent/agent.utils';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import styles from './AgentMessage.module.scss';
import { Props } from './AgentMessage.types';

const AgentMessage = ({ message }: Props) => {
  const { t } = useTranslation();
  const assetId = useCurrentAssetId();

  // Every document card opens the documents page in the right folder (and the file, if any).
  const documentCard = (
    result: Extract<AgentToolResult, { kind: 'document' | 'folder' | 'documents' }>
  ) => {
    if (result.kind === 'folder') {
      return {
        to: documentsLink(assetId, { path: result.folder.path }),
        icon: <IconFolderPlus size={18} />,
        label: t('agent.folderCreated', { name: folderLabel(result.folder.path) }),
      };
    }
    if (result.kind === 'documents') {
      return {
        to: documentsLink(assetId, { path: result.folder }),
        icon:
          result.action === 'moved' ? <IconFolderSymlink size={18} /> : <IconPencil size={18} />,
        label:
          result.action === 'moved' && result.folder
            ? t('agent.documentsMoved', { count: result.count, name: folderLabel(result.folder) })
            : t('agent.documentsRenamed', { count: result.count }),
      };
    }
    const { document } = result;
    return document.type === 'folder'
      ? {
          to: documentsLink(assetId, { path: document.path }),
          icon: <IconFolder size={18} />,
          label: t('agent.folderOpen', { name: document.name }),
        }
      : {
          to: documentsLink(assetId, { path: document.folder, open: document.id }),
          icon: <IconFile size={18} />,
          label: t('agent.documentOpen', { name: document.name }),
        };
  };

  const renderToolCard = (result: AgentToolResult, key: number) => {
    if (result.kind === 'document' || result.kind === 'folder' || result.kind === 'documents') {
      const { to, icon, label } = documentCard(result);
      return (
        <Link key={key} className={styles.toolCard} to={to}>
          {icon}
          {label}
        </Link>
      );
    }
    // Nothing left to open once deleted: a plain card, no link.
    if (result.kind === 'event' && result.action === 'deleted') {
      return (
        <div key={key} className={styles.toolCard}>
          <IconCalendarMinus size={18} />
          {t(
            result.event.scope === 'occurrence' ? 'agent.occurrenceDeleted' : 'agent.eventDeleted',
            { name: result.event.name }
          )}
        </div>
      );
    }
    if (result.kind === 'task') {
      if (result.action === 'deleted') {
        return (
          <div key={key} className={styles.toolCard}>
            <IconTrash size={18} />
            {t('agent.taskDeleted', { name: result.task.title })}
          </div>
        );
      }
      const isDone = result.action === 'updated' && result.task.status === 'DONE';
      const icon =
        result.action === 'created' ? (
          <IconChecklist size={18} />
        ) : isDone ? (
          <IconCircleCheck size={18} />
        ) : (
          <IconEdit size={18} />
        );
      const labelKey =
        result.action === 'created'
          ? 'agent.taskCreated'
          : isDone
            ? 'agent.taskDone'
            : 'agent.taskUpdated';
      return (
        <Link
          key={key}
          className={styles.toolCard}
          to={`/asset/${assetId}/tasks/${result.task.id}`}
        >
          {icon}
          {t(labelKey, { name: result.task.title })}
        </Link>
      );
    }
    const created = result.action === 'created';
    const { to, icon, label } =
      result.kind === 'contact'
        ? {
            to: `/asset/${assetId}/infos-et-contacts/contacts/${result.contact.id}`,
            icon: created ? <IconUserPlus size={18} /> : <IconUserEdit size={18} />,
            label: t(created ? 'agent.contactCreated' : 'agent.contactUpdated', {
              name:
                result.contact.displayName ??
                [result.contact.firstName, result.contact.lastName].filter(Boolean).join(' '),
            }),
          }
        : {
            to: `/asset/${assetId}/planning/event/${result.event.id}`,
            icon: created ? <IconCalendarPlus size={18} /> : <IconCalendarRepeat size={18} />,
            label: t(created ? 'agent.eventCreated' : 'agent.eventUpdated', {
              name: result.event.name,
            }),
          };
    return (
      <Link key={key} className={styles.toolCard} to={to}>
        {icon}
        {label}
      </Link>
    );
  };

  return (
    <div
      className={classNames(
        styles.container,
        message.role === 'user' ? styles.user : styles.assistant
      )}
    >
      {message.parts.map((part, index) => {
        if (part.type === 'text') {
          // Mistral answers in markdown despite the prompt: render it rather than show the asterisks.
          return message.role === 'assistant' ? (
            <div key={index} className={classNames(styles.text, styles.markdown)}>
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node: _node, ...props }) => (
                    <a {...props} target="_blank" rel="noreferrer" />
                  ),
                }}
              >
                {part.text}
              </Markdown>
            </div>
          ) : (
            <p key={index} className={styles.text}>
              {part.text}
            </p>
          );
        }
        if (part.type !== 'dynamic-tool') {
          return null;
        }
        const result = getAgentToolResult(part);
        if (result) {
          return renderToolCard(result, index);
        }
        return null;
      })}
    </div>
  );
};

export default AgentMessage;
